import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { routes } from "@bezoned/shared/routes.js";
import { useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import { ApplicationError } from "../components/ui/application-error.js";
import { FeatureFlagsProvider } from "../features/feature-flags/feature-flags.provider.js";
import { TenantExistsGuard } from "../features/onboarding/tenant-exists-guard.js";
import { SubscriptionGuard } from "../features/subscription/subscription-guard.js";
import { SubscriptionProvider } from "../features/subscription/subscription.provider.js";
import { AdminConsentEndPage } from "../pages/admin-consent-end-page.js";
import { AdminConsentStartPage } from "../pages/admin-consent-start-page.js";
import { ChooseTeamPage } from "../pages/choose-team-page.js";
import { MeetingSidePanelConfigurationPage } from "../pages/meeting-side-panel-configuration-page.js";
import { MeetingSidePanelPage } from "../pages/meeting-side-panel-page.js";
import { OfficePage } from "../pages/office-page.js";
import { PermissionUpgradePage } from "../pages/permission-upgrade-page.js";
import { TeamInstallationPage } from "../pages/team-installation-page.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { withFluidFramework } from "./with-fluid-framework.js";

// Create wrapped components
const OfficeWithFluid = withFluidFramework(OfficePage);

const AppWithTransition = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const transitionChildRef = useRef(null);
  const handleError = useStableCallback((error, info) => {
    recordErrorInSentry(error, {
      data: { componentStack: info.componentStack },
    });
  });

  return (
    <div
      style={{
        height: "100vh",
        overflow: "hidden",
        position: "relative",
        width: "100vw",
      }}
    >
      <ErrorBoundary FallbackComponent={ApplicationError} onError={handleError}>
        <TransitionGroup component={null}>
          <CSSTransition
            classNames="fade"
            key={location.pathname}
            nodeRef={transitionChildRef}
            timeout={180}
          >
            <div ref={transitionChildRef}>{children}</div>
          </CSSTransition>
        </TransitionGroup>
      </ErrorBoundary>
    </div>
  );
};

const RedirectToChooseTeam = () => {
  const location = useLocation();

  return (
    <Navigate replace to={`${routes.protected.chooseTeam}${location.search}`} />
  );
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => (
  <AppWithTransition>{children}</AppWithTransition>
);

const ProtectedRoute = () => (
  <AppWithTransition>
    <FeatureFlagsProvider>
      <TenantExistsGuard>
        <SubscriptionProvider>
          <Outlet />
        </SubscriptionProvider>
      </TenantExistsGuard>
    </FeatureFlagsProvider>
  </AppWithTransition>
);

export const routeConfig = [
  {
    element: (
      <PublicRoute>
        <AdminConsentStartPage flow="onboarding" />
      </PublicRoute>
    ),
    path: routes.public.auth.adminConsent.onboardingStart,
  },
  {
    element: (
      <PublicRoute>
        <AdminConsentEndPage flow="onboarding" />
      </PublicRoute>
    ),
    path: routes.public.auth.adminConsent.onboardingEnd,
  },
  {
    element: (
      <PublicRoute>
        <AdminConsentStartPage flow="permission-upgrade" />
      </PublicRoute>
    ),
    path: routes.public.auth.adminConsent.permissionUpgradeStart,
  },
  {
    element: (
      <PublicRoute>
        <AdminConsentEndPage flow="permission-upgrade" />
      </PublicRoute>
    ),
    path: routes.public.auth.adminConsent.permissionUpgradeEnd,
  },
  {
    element: (
      <PublicRoute>
        <TeamInstallationPage />
      </PublicRoute>
    ),
    path: routes.public.team,
  },

  // Protected routes
  {
    children: [
      {
        element: (
          <SubscriptionGuard>
            <ChooseTeamPage />
          </SubscriptionGuard>
        ),
        path: routes.protected.chooseTeam,
      },
      {
        element: (
          <SubscriptionGuard>
            <PermissionUpgradePage />
          </SubscriptionGuard>
        ),
        path: routes.protected.permissionUpgrade,
      },
      {
        element: (
          <SubscriptionGuard>
            <OfficeWithFluid />
          </SubscriptionGuard>
        ),
        path: routes.protected.office,
      },
      {
        element: <MeetingSidePanelConfigurationPage />,
        path: routes.protected.meetingSidePanelConfiguration,
      },
      {
        element: <MeetingSidePanelPage />,
        path: routes.protected.meetingSidePanel,
      },
    ],
    element: <ProtectedRoute />,
  },

  // Redirects
  {
    element: (
      <AppWithTransition>
        <RedirectToChooseTeam />
      </AppWithTransition>
    ),
    path: "/",
  },
  {
    element: (
      <AppWithTransition>
        <RedirectToChooseTeam />
      </AppWithTransition>
    ),
    path: "*",
  },
];

export const router = createBrowserRouter(routeConfig, {
  future: {
    v7_relativeSplatPath: true,
  },
});
